import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDashboard } from '../../context/DashboardContext';
import { useAuth } from '../../auth/AuthContext';
import { useUserRole, useCanEditStylist } from '../../hooks/useUserRole';
import { useGoalsConfig } from '../../utils/goalsLocalStore';
import { useNavigateWithFilters } from '../../utils/dashboardNav';
import DevFilterBar from '../layout/DevFilterBar';
import { buildKurocoHomeData, SC_TO_METRIC } from '../../adapters/kurocoData';
import { fmt, EM } from '../kuroco/fmt';
import LineChart from '../kuroco/LineChart';
import MonthPicker from '../kuroco/MonthPicker';
import ActionPlanCard from '../goals/ActionPlanCard';
import StoreBreakdown from '../kuroco/StoreBreakdown';
import UpdatesBanner from './UpdatesBanner';
import CheckinPopup from './CheckinPopup';
import { greetingFor } from '../../utils/callName';
import { fetchActionCheckins, fetchGoals, fetchShift, saveActionCheckin, setShiftDay } from '../../api/goalsApi';
import { notifyGoalsUpdated } from '../../utils/goalsLocalStore';
import { checkinPopupEligible, shouldShowCheckinPopup } from '../../utils/checkinPopup';
import { addDays, tokyoNowHHMM, tokyoToday } from '../../utils/tokyoDate';

const KPI_ORDER_STORAGE = 'kuroco-kpi-order';

/** Shimmer lines shaped like the text they stand in for (same look as .aigp-skel-line). */
function SkelLines({ widths = [90, 80, 60] }) {
  return (
    <div className="skel-lines" aria-hidden="true">
      {widths.map((w, i) => (
        <span key={i} className="skel skel-line" style={{ width: `${w}%` }} />
      ))}
    </div>
  );
}

/**
 * First paint before the numbers arrive: the real layout with shimmer placeholders
 * (progress block, 注力指標, AIコメント, chart), so nothing jumps when data lands and the
 * month picker / dev filters stay usable. Normally only seen on a brand-new day or tab —
 * the day snapshot in DashboardContext paints yesterday's figures straight away.
 */
function HomeSkeleton({ isAdmin, selectedMonth, setSelectedMonth, months, formatMonthDisplay }) {
  return (
    <div className="fade" aria-busy="true">
      {isAdmin && <DevFilterBar />}
      <div className="home-period-row">
        <div className="databanner">
          <span className="dot" />
          <span>実績を読み込み中…</span>
        </div>
        <MonthPicker
          value={selectedMonth}
          onChange={setSelectedMonth}
          availableMonths={months}
          formatMonthDisplay={formatMonthDisplay}
          title="表示月を選択"
          className="home-month-picker"
        />
      </div>
      <div className="fv-progress skel-progress">
        <div className="fv-prog-top">
          <span className="fv-prog-label">売上目標進捗</span>
          <span className="skel skel-pill" />
        </div>
        <div className="fv-prog-bar"><div className="fv-prog-fill" style={{ width: '0%' }} /></div>
        <div className="fv-prog-figs">
          {['実績', '目標', '残り'].map((k) => (
            <div className="fv-fig" key={k}>
              <span className="fv-fig-k">{k}</span>
              <span className="skel skel-fig" />
            </div>
          ))}
        </div>
      </div>
      <div className="fv-kpis">
        <div className="fv-kpis-title">注力指標</div>
        <div className="fv-kpi-grid fv-kpi-grid--3">
          {[0, 1, 2].map((i) => (
            <div className="fv-kpi" key={i}>
              <span className="skel skel-line" style={{ width: '70%' }} />
              <span className="skel skel-val" />
              <span className="skel skel-line" style={{ width: '50%' }} />
            </div>
          ))}
        </div>
      </div>
      <div className="ai-summary">
        <div className="ai-summary-head">
          <span className="ai-avatar">M</span>
          <span className="ai-summary-head-t">AIマネージャーより</span>
        </div>
        <div className="ai-summary-body"><SkelLines /></div>
      </div>
      <div className="section-title" style={{ marginBottom: 11 }}>時系列分析</div>
      <div className="chart-card">
        <div className="chart-head"><span className="skel skel-line" style={{ width: '40%' }} /></div>
        <div className="chart-canvas"><div className="skel skel-chart" /></div>
      </div>
    </div>
  );
}

function AiSummaryBody({ loading, isHtml, body }) {
  if (loading) {
    return <SkelLines />;
  }

  if (isHtml) {
    return <span dangerouslySetInnerHTML={{ __html: body }} />;
  }

  return <div className="ai-summary-body-text">{body || ''}</div>;
}

export default function HomeScreen({ onGoTab }) {
  const { isAdmin } = useUserRole();
  const { user } = useAuth();
  const {
    kpis,
    dataAsOf,
    kpiBreakdown,
    kpiHistory,
    mlForecasts,
    aiComment,
    loading,
    selectedMonth,
    selectedStore,
    selectedStylist,
    months,
    setSelectedMonth,
    formatMonthDisplay,
    myProfile,
  } = useDashboard();
  const [goals] = useGoalsConfig(user?.uid, selectedStore, selectedStylist);
  const canEditStylist = useCanEditStylist(selectedStylist);

  // ── 行動記録 popup: from 18:00 Tokyo, own home, approved plan, today not recorded. × closes it for now only ──
  // githubDOCS/TASKS/2026-10-26_ホームの行動記録ポップアップ_18時以降未記録の人に出す.md
  const [checkinOpen, setCheckinOpen] = useState(false);
  const [checkinByDay, setCheckinByDay] = useState({}); // today + yesterday, for the popup's 昨日／今日 tabs
  const [offByDay, setOffByDay] = useState({}); // {[ymd]: true} days off (shift) for today + yesterday
  const [checkinToast, setCheckinToast] = useState(false);
  const [checkinVersion, setCheckinVersion] = useState(0); // bumps the 7-day strip after a save
  const checkinToday = tokyoToday();
  const checkinParams = {
    nowHHMM: tokyoNowHHMM(),
    // useCanEditStylist is also true for dev; the popup is for the person's own record only
    isOwn: canEditStylist && Boolean(myProfile?.stylist_name_pos) && selectedStylist === myProfile.stylist_name_pos,
    goals,
  };
  const checkinEligible = checkinPopupEligible(checkinParams);
  useEffect(() => {
    if (!checkinEligible) {
      setCheckinOpen(false);
      return undefined;
    }
    let cancelled = false;
    const yesterday = addDays(checkinToday, -1);
    Promise.all([
      fetchActionCheckins({ store: selectedStore, stylist: selectedStylist, start: yesterday, end: checkinToday, limit: 2 }),
      // shift unknown (old backend, network) → treat as working days; the record decides
      fetchShift({ store: selectedStore, stylist: selectedStylist, start: yesterday, end: checkinToday }).catch(() => null),
    ])
      .then(([res, shift]) => {
        if (cancelled) return;
        const byDay = {};
        (res?.checkins || []).forEach((c) => { if (c?.date) byDay[c.date] = c; });
        const off = {};
        (shift?.offDays || []).forEach((d) => { off[d] = true; });
        setCheckinByDay(byDay);
        setOffByDay(off);
        setCheckinOpen(shouldShowCheckinPopup({ ...checkinParams, todayRecorded: Boolean(byDay[checkinToday]), todayOff: Boolean(off[checkinToday]) }));
      })
      .catch(() => {}); // record unknown → don't show
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- checkinParams is rebuilt each render; eligibility + who/when decide
  }, [checkinEligible, selectedStore, selectedStylist, checkinToday]);
  useEffect(() => {
    if (!checkinToast) return undefined;
    const t = setTimeout(() => setCheckinToast(false), 4000);
    return () => clearTimeout(t);
  }, [checkinToast]);

  const saveCheckin = async (day, entries) => {
    const res = await saveActionCheckin({
      store: selectedStore, stylist: selectedStylist, date: day, entries, source: 'home_popup',
    });
    setCheckinByDay((p) => ({ ...p, [day]: res?.checkin || { date: day, entries } }));
    setCheckinVersion((v) => v + 1);
    // 「今月 N回」 moved on the server — refresh the plan counters (same as CheckinWeekStrip).
    const g = await fetchGoals({ store: selectedStore, stylist: selectedStylist }).catch(() => null);
    if (g?.goals) notifyGoalsUpdated(g.goals);
  };
  // 「この日は休み」 in the popup: one-off day off → no reminder push tonight, 「休」 in the 7-day strip.
  const markCheckinOff = async (day, off = true) => {
    await setShiftDay({ store: selectedStore, stylist: selectedStylist, date: day, off });
    setOffByDay((p) => ({ ...p, [day]: off }));
    setCheckinVersion((v) => v + 1);
  };
  const dismissCheckin = () => {
    setCheckinOpen(false);
    setCheckinToast(true);
  };
  // Own record → the saved 呼び方 (callName.js, same rule as the AI). Someone else's → their POS name.
  const greet = greetingFor(myProfile, selectedStylist);
  const callName = greet.isOwn ? greet.name : null;

  const D = useMemo(
    () =>
      buildKurocoHomeData({
        kpis,
        kpiHistory,
        mlForecasts,
        aiComment,
        loading,
        selectedMonth,
        stylistName: selectedStylist,
        callName,
        goals,
        dataAsOf,
      }),
    [kpis, dataAsOf, kpiHistory, mlForecasts, aiComment, loading, selectedMonth, selectedStylist, callName, goals]
  );
  const navigate = useNavigateWithFilters();
  const defaultOrder = D.kpis.map((k) => k.key);
  const [metricKey, setMetricKey] = useState('revenue');
  const [kpiOrder, setKpiOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(KPI_ORDER_STORAGE) || 'null');
      if (
        Array.isArray(saved) &&
        saved.every((k) => defaultOrder.includes(k)) &&
        saved.length === defaultOrder.length
      ) {
        return saved;
      }
    } catch {
      /* ignore */
    }
    return defaultOrder;
  });
  const [reorderMode, setReorderMode] = useState(false);
  const [scExpanded, setScExpanded] = useState(false);
  const chartRef = useRef(null);
  const cardRefs = useRef({});
  const dragState = useRef({ key: null, scale: 1, offX: 0, offY: 0 });
  const [dragKey, setDragKey] = useState(null);
  const [dragXY, setDragXY] = useState({ x: 0, y: 0 });
  const kpiOrderRef = useRef(kpiOrder);

  useEffect(() => {
    kpiOrderRef.current = kpiOrder;
  }, [kpiOrder]);

  useEffect(() => {
    if (!defaultOrder.length) return;
    setKpiOrder((prev) => {
      const present = new Set(defaultOrder);
      const kept = prev.filter((k) => present.has(k));
      const added = defaultOrder.filter((k) => !prev.includes(k));
      const next = [...kept, ...added];
      if (next.length === prev.length && next.every((k, i) => k === prev[i])) return prev;
      return next;
    });
  }, [defaultOrder.join('|')]);

  useEffect(() => {
    if (D.metricOrder.length && !D.metricOrder.includes(metricKey)) {
      setMetricKey(D.metricOrder[0]);
    }
  }, [D.metricOrder, metricKey]);

  const metric = D.metrics[metricKey] || D.metrics[D.metricOrder[0]] || null;
  const yen = (v) => (v != null && v !== EM ? fmt('currency', v) : EM);

  const persistOrder = (next) => {
    setKpiOrder(next);
    localStorage.setItem(KPI_ORDER_STORAGE, JSON.stringify(next));
  };

  const moveKpi = (key, dir) => {
    const i = kpiOrder.indexOf(key);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= kpiOrder.length) return;
    const next = kpiOrder.slice();
    [next[i], next[j]] = [next[j], next[i]];
    persistOrder(next);
  };

  const resetOrder = () => persistOrder(defaultOrder);

  const kpiByKey = Object.fromEntries(D.kpis.map((k) => [k.key, k]));
  const orderedKpis = (kpiOrder.length ? kpiOrder : defaultOrder)
    .map((k) => kpiByKey[k])
    .filter(Boolean);

  const readNaturalCenter = (el) => {
    const prev = el.style.transform;
    el.style.transform = '';
    const r = el.getBoundingClientRect();
    el.style.transform = prev;
    return { cx: r.left + r.width / 2, cy: r.top + r.height / 2 };
  };

  const onCardPointerDown = (e, key) => {
    if (!reorderMode) return;
    if (e.target.closest('.sc-arrow')) return;
    const el = cardRefs.current[key];
    if (!el) return;
    e.preventDefault();
    const stage = document.querySelector('.kuroco-shell');
    let scale = 1;
    if (stage) {
      try {
        scale = new DOMMatrixReadOnly(getComputedStyle(stage).transform).a || 1;
      } catch {
        scale = 1;
      }
    }
    const nat = readNaturalCenter(el);
    dragState.current = {
      key,
      scale,
      offX: (e.clientX - nat.cx) / scale,
      offY: (e.clientY - nat.cy) / scale,
    };
    setDragKey(key);
    setDragXY({ x: dragState.current.offX, y: dragState.current.offY });
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const onCardPointerMove = (e) => {
    const ds = dragState.current;
    if (!ds.key) return;
    const el = cardRefs.current[ds.key];
    if (!el) return;
    const nat = readNaturalCenter(el);
    const dx = (e.clientX - nat.cx) / ds.scale;
    const dy = (e.clientY - nat.cy) / ds.scale;
    setDragXY({ x: dx, y: dy });

    const order = kpiOrderRef.current;
    for (const key of order) {
      if (key === ds.key) continue;
      const otherEl = cardRefs.current[key];
      if (!otherEl) continue;
      const r = otherEl.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) continue;
      const next = order.slice();
      const i = next.indexOf(ds.key);
      const j = next.indexOf(key);
      if (i < 0 || j < 0) break;
      [next[i], next[j]] = [next[j], next[i]];
      persistOrder(next);
      break;
    }
  };

  const onCardPointerUp = () => {
    dragState.current.key = null;
    setDragKey(null);
    setDragXY({ x: 0, y: 0 });
  };

  const handleScCard = (k) => {
    const mapped = SC_TO_METRIC[k.key];
    if (!mapped || !D.metrics[mapped]) return;
    setMetricKey(mapped);
    setTimeout(() => {
      if (chartRef.current) {
        const scroll = chartRef.current.closest('.scroll');
        if (scroll) scroll.scrollTop = chartRef.current.offsetTop - 80;
      }
    }, 60);
  };

  const goAi = () => onGoTab && onGoTab('ai');
  const progressWidth =
    typeof D.sales.progress === 'number' ? `${Math.min(100, D.sales.progress)}%` : '0%';

  if (D.isLoading && !kpis) {
    return (
      <HomeSkeleton
        isAdmin={isAdmin}
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
        months={months}
        formatMonthDisplay={formatMonthDisplay}
      />
    );
  }

  return (
    <div className="fade">
      {isAdmin && <DevFilterBar />}
      <UpdatesBanner />
      {checkinOpen && (
        <CheckinPopup
          today={checkinToday}
          actions={goals?.actions || []}
          recordByDay={checkinByDay}
          offByDay={offByDay}
          onSave={saveCheckin}
          onMarkOff={markCheckinOff}
          onDismiss={dismissCheckin}
          onDone={() => setCheckinOpen(false)}
        />
      )}
      {checkinToast && (
        <div className="cp-toast" role="status">
          <span>ホームの「記録を編集」からいつでも記録できます</span>
        </div>
      )}
      <div className="home-period-row">
        <div className="databanner">
          <span className="dot" />
          <span>昨日 {D.period.dataAsOf} までの実績</span>
        </div>
        <MonthPicker
          value={selectedMonth}
          onChange={setSelectedMonth}
          availableMonths={months}
          formatMonthDisplay={formatMonthDisplay}
          title="表示月を選択"
          className="home-month-picker"
        />
      </div>

      <div className="fv-progress">
        <div className="fv-prog-top">
          <span className="fv-prog-label">
            売上目標進捗
            {goals?.goalsApprovalStatus === 'pending' && (
              // 目標・注力の変更をマネージャーが確認中（値はもう反映されている）
              <span className="plan-badge plan-badge--pending">確認中</span>
            )}
          </span>
          <span className="fv-prog-pct">
            <span className="fv-prog-pct-k">達成率</span>
            <span style={{ color: 'rgb(255, 255, 255)' }}>{D.sales.progressDisplay}</span>
            {typeof D.sales.progress === 'number' && <small>%</small>}
          </span>
        </div>
        <div className="fv-prog-bar">
          <div className="fv-prog-fill" style={{ width: progressWidth }} />
        </div>
        <div className="fv-prog-figs">
          <div className="fv-fig">
            <span className="fv-fig-k">実績</span>
            <span className="fv-fig-v">{yen(D.sales.actual)}</span>
          </div>
          <div className="fv-fig">
            <span className="fv-fig-k">目標</span>
            <span className="fv-fig-v">{yen(D.sales.target)}</span>
          </div>
          <div className="fv-fig">
            <span className="fv-fig-k">残り</span>
            <span className="fv-fig-v accent">{yen(D.sales.gap)}</span>
          </div>
        </div>
        <StoreBreakdown breakdown={kpiBreakdown} onDark />
        <div className="fv-prog-cmp">
          <span>
            前月 <b className={D.sales.cmpTone === 'down' ? 'down' : 'up'}>{D.sales.prev}</b>
          </span>
          <span className="sep">·</span>
          <span>
            前年 <b className={D.sales.cmpTone === 'down' ? 'down' : 'up'}>{D.sales.year}</b>
          </span>
        </div>
        {D.subKgi && (
          // 追加KGI (指名率 / 指名数) — same card as 売上, it is a KGI too
          <div className="fv-prog-sub">
            <div className="fv-prog-sub-top">
              <span className="fv-prog-sub-k">{D.subKgi.label}</span>
              <span className="fv-prog-sub-v">
                {D.subKgi.actualDisplay}
                <span className="fv-prog-sub-t"> / 目標 {D.subKgi.targetDisplay}</span>
              </span>
              <span className="fv-prog-sub-pct">
                達成率 <b>{D.subKgi.progress != null ? `${D.subKgi.progress}%` : EM}</b>
              </span>
            </div>
            <div className="fv-prog-bar fv-prog-bar--sub">
              <div className="fv-prog-fill" style={{ width: `${Math.min(100, D.subKgi.progress ?? 0)}%` }} />
            </div>
          </div>
        )}
      </div>

      <div className="fv-kpis">
        <div className="fv-kpis-title">注力指標</div>
        {/* Only the 注力 that are set (owner, 2026-09-30) — adding happens in 目標 / 設定, never here */}
        {D.focusMetrics.length === 0 && (
          <div style={{ padding: '4px 0 8px', fontSize: '12px', color: 'var(--muted)' }}>
            注力指標は未設定です（目標タブまたは設定から選べます）
          </div>
        )}
        <div className={`fv-kpi-grid${D.focusMetrics.length === 3 ? ' fv-kpi-grid--3' : ''}`}>
          {D.focusMetrics.map((k) => (
            <div className="fv-kpi" key={k.key}>
              <div className="fv-kpi-top">
                <span className="fv-kpi-ic">{k.icon}</span>
                <span className="fv-kpi-label">{k.label}</span>
              </div>
              <span className="fv-kpi-val">{k.value}</span>
              <span className="fv-kpi-tgt-sub">目標 {k.target}</span>
              <span className="fv-kpi-cmp gap">
                <span className="fv-kpi-cmp-k">目標差</span>
                {k.gap}
              </span>
              <span className={`fv-kpi-cmp ${k.tone}`}>
                <span className="fv-kpi-cmp-k">前月</span>
                {k.prev}
                <span className="fv-kpi-cmp-k" style={{ marginLeft: 4 }}>前年</span>
                {k.year}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="ai-summary">
        <div className="ai-summary-head">
          <span className="ai-avatar">M</span>
          <span className="ai-summary-head-t">AIマネージャーより</span>
        </div>
        <div className="ai-summary-body">
          <AiSummaryBody
            key={D.aiSummary.body || 'loading'}
            loading={D.aiSummary.loading}
            isHtml={D.aiSummary.isHtml}
            body={D.aiSummary.body}
          />
        </div>
      </div>

      <div className="quick">
        <button type="button" className="quick-btn quick-btn--primary" onClick={goAi}>
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M4 20h16M5 16l9.5-9.5a2 2 0 013 3L8 19l-4 1 1-4z"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          行動を記録する
        </button>
        <button type="button" className="quick-btn quick-btn--ghost" onClick={goAi}>
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M21 14a2 2 0 01-2 2H8l-4 4V5a2 2 0 012-2h13a2 2 0 012 2z"
              stroke="var(--brand-deep)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          AIに相談する
        </button>
      </div>

      <div>
        <div className="section-title" style={{ marginBottom: 11 }}>
          <span>スコアカード</span>
          <span className="sc-actions">
            {reorderMode && (
              <button type="button" className="sc-reset" onClick={resetOrder}>
                初期化
              </button>
            )}
            <button
              type="button"
              className={`sc-reorder${reorderMode ? ' is-on' : ''}`}
              onClick={() => setReorderMode(!reorderMode)}
            >
              {reorderMode ? (
                '完了'
              ) : (
                <>
                  <svg viewBox="0 0 14 14" width="12" height="12" fill="none" aria-hidden="true">
                    <circle cx="3" cy="3" r="1.2" fill="currentColor" />
                    <circle cx="7" cy="3" r="1.2" fill="currentColor" />
                    <circle cx="11" cy="3" r="1.2" fill="currentColor" />
                    <circle cx="3" cy="7" r="1.2" fill="currentColor" />
                    <circle cx="7" cy="7" r="1.2" fill="currentColor" />
                    <circle cx="11" cy="7" r="1.2" fill="currentColor" />
                    <circle cx="3" cy="11" r="1.2" fill="currentColor" />
                    <circle cx="7" cy="11" r="1.2" fill="currentColor" />
                    <circle cx="11" cy="11" r="1.2" fill="currentColor" />
                  </svg>
                  並び替え
                </>
              )}
            </button>
          </span>
        </div>
        <div className={`sc-grid${reorderMode ? ' sc-grid--reorder' : ''}`}>
          {orderedKpis.map((k, idx) => {
            const mapped = SC_TO_METRIC[k.key];
            const isActive = mapped && mapped === metricKey;
            const isFirst = idx === 0;
            const isLast = idx === orderedKpis.length - 1;
            const isDragging = dragKey === k.key;
            const hidden = !reorderMode && !scExpanded && idx >= 6;
            const cardStyle = isDragging
              ? {
                  transform: `translate(${dragXY.x}px, ${dragXY.y}px) scale(1.06)`,
                  zIndex: 10,
                  transition: 'none',
                  boxShadow: '0 12px 28px rgba(28,70,52,.22)',
                  opacity: 0.96,
                  animation: 'none',
                }
              : undefined;
            if (hidden) return null;
            return (
              <button
                key={k.key}
                type="button"
                ref={(el) => {
                  cardRefs.current[k.key] = el;
                }}
                className={`sc-card${isActive && !reorderMode ? ' sc-card--active' : ''}${isDragging ? ' sc-card--dragging' : ''}`}
                style={cardStyle}
                onClick={() => {
                  if (!reorderMode) handleScCard(k);
                }}
                onPointerDown={(e) => onCardPointerDown(e, k.key)}
                onPointerMove={onCardPointerMove}
                onPointerUp={onCardPointerUp}
                onPointerCancel={onCardPointerUp}
              >
                {reorderMode && (
                  <span
                    className="sc-arrows"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    role="presentation"
                  >
                    <span
                      className="sc-arrow"
                      onClick={() => moveKpi(k.key, -1)}
                      aria-disabled={isFirst}
                      role="button"
                      tabIndex={0}
                    >
                      <svg viewBox="0 0 10 8" width="10" height="8" fill="none" aria-hidden="true">
                        <path d="M1 6l4-4 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <span
                      className="sc-arrow"
                      onClick={() => moveKpi(k.key, +1)}
                      aria-disabled={isLast}
                      role="button"
                      tabIndex={0}
                    >
                      <svg viewBox="0 0 10 8" width="10" height="8" fill="none" aria-hidden="true">
                        <path d="M1 2l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </span>
                )}
                <div className="sc-label">{k.label}</div>
                <div className="sc-val">{k.value}</div>
                <div className="sc-cmp">
                  <span className="sc-cmp-k">前月</span>
                  <span className={`sc-delta ${k.toneP}`}>{k.prev}</span>
                </div>
                <div className="sc-cmp">
                  <span className="sc-cmp-k">前年</span>
                  <span className={`sc-delta ${k.toneY}`}>{k.year}</span>
                </div>
              </button>
            );
          })}
        </div>
        {!reorderMode && orderedKpis.length > 6 && (
          <button
            type="button"
            className="sc-more"
            onClick={() => setScExpanded((v) => !v)}
            aria-expanded={scExpanded}
          >
            {scExpanded ? (
              <>
                <span>閉じる</span>
                <svg width="11" height="7" viewBox="0 0 11 7" fill="none" aria-hidden="true">
                  <path d="M1.5 5.5L5.5 1.5L9.5 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </>
            ) : (
              <>
                <span>
                  もっと見る <span className="sc-more-n">+{orderedKpis.length - 6}</span>
                </span>
                <svg width="11" height="7" viewBox="0 0 11 7" fill="none" aria-hidden="true">
                  <path d="M1.5 1.5L5.5 5.5L9.5 1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </>
            )}
          </button>
        )}
      </div>

      <div ref={chartRef}>
        <div className="section-title" style={{ marginBottom: 11 }}>
          時系列分析
          <span className="muted">タップで指標を切替</span>
        </div>
        <div className="chart-card">
          <div className="chart-head">
            {loading.kpiHistory && !(kpiHistory && kpiHistory.length) ? (
              <span className="skel skel-line" style={{ width: '40%' }} aria-label="読み込み中" />
            ) : (
              <select
                className="chart-select"
                value={metricKey}
                onChange={(e) => setMetricKey(e.target.value)}
              >
                {D.metricOrder.map((key) => (
                  <option key={key} value={key}>
                    {D.metrics[key]?.label}
                  </option>
                ))}
              </select>
            )}
            {metric?.target != null && (
              <span className="chart-target">目標 {fmt(metric.unit, metric.target)}</span>
            )}
          </div>
          {metric && (
            <>
              <div className="chart-verdict">
                <span className="ai-avatar">M</span>
                <div className="chart-verdict-t" dangerouslySetInnerHTML={{ __html: metric.verdict }} />
              </div>
              <div className="chart-canvas">
                <LineChart metric={metric} months={D.chartMonths} />
              </div>
              <div className="chart-legend">
                <span>
                  <i className="lg-line" /> 実績
                </span>
                <span>
                  <i className="lg-line dash" /> 現状の予測
                </span>
                <span>
                  <i className="lg-line need" /> 目標達成に必要
                </span>
                <span>
                  <i className="lg-line tgt" /> 目標
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* moved from 目標 (2026-09-30) */}
      <ActionPlanCard
        actions={D.actions || []}
        goals={goals}
        store={selectedStore}
        stylist={selectedStylist}
        canEdit={canEditStylist}
        onEdit={() => navigate('/settings/actionedit')}
        checkinVersion={checkinVersion}
      />
    </div>
  );
}
